import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';

import Accordion from '@/components/Accordion/Accordion';
import { Flex, VFlex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import service from '@/services/index';

import TaxonomyCard from './TaxonomyCard';
import TopicCard from './TopicCard';
import TopicCardContainer from './TopicCardContainer';

type Props = {
  target: CreateTopicTarget;
  targetTopicAlias?: string;
  taxonomyAlias: string;
};

const UpperLevelDisplay = (props: Props) => {
  const { data } = useQuery({
    queryKey: [...queryKeys.taxonomy.TOPIC_DETAIL, props.targetTopicAlias],
    queryFn: () => {
      if (!props.targetTopicAlias) throw new Error('Target alias must be set');
      return service.taxonomy.fetchTopicDetail(props.targetTopicAlias);
    },
    enabled: props.target === 'topic',
  });

  const lv1TopicAlias = data?.parent_topic_lv1?.parent_id;
  const lv2TopicAlias = data?.parent_topic_lv2?.parent_id;
  const lv3TopicAlias = data?.parent_topic_lv3?.parent_id;

  return (
    <Style>
      <Accordion
        anchor={(isOpen) => (
          <AnchorStyle
            a="center"
            j="space-between"
            g={6}
            style={{
              cursor: 'pointer',
              width: '100%',
            }}
          >
            <Flex a="center" g={6}>
              <Typo type="label2">Topic hierarchy</Typo>
              <Flex style={{ rotate: isOpen ? '0deg' : '180deg' }}>
                <InfoCircleOutlined />
              </Flex>
            </Flex>
            <DownOutlined />
          </AnchorStyle>
        )}
        content={
          <ContentWrapper>
            <TaxonomyCard alias={props.taxonomyAlias} />
            {lv1TopicAlias && <TopicCardContainer alias={lv1TopicAlias} />}
            {lv2TopicAlias && <TopicCardContainer alias={lv2TopicAlias} />}
            {lv3TopicAlias && <TopicCardContainer alias={lv3TopicAlias} />}
            {data && <TopicCard topic={data} />}
          </ContentWrapper>
        }
      />
    </Style>
  );
};

const Style = styled(VFlex)`
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: ${(props) => props.theme.radius.panel}px;
`;

const AnchorStyle = styled(Flex)`
  padding: 14px 24px;
  color: ${(props) => props.theme.colors.gray600};
`;

const ContentWrapper = styled(VFlex)`
  padding: 0px 24px 24px;
  gap: 8px;
`;

export default UpperLevelDisplay;
